import { DropdownElements } from './_dom-manager'
import { DropdownState, DropdownOptions, DropdownOptionType } from './types'

/**
 * Класс для управления состоянием dropdown
 */
export class DropdownStateManager {
  private element: HTMLElement
  private elements: DropdownElements
  private state: DropdownState
  private options: DropdownOptions

  /**
   * Создаёт экземпляр DropdownStateManager
   *
   * @param {HTMLElement} element - Корневой элемент
   * @param {DropdownElements} elements - DOM элементы
   * @param {DropdownState} state - Состояние
   * @param {DropdownOptions} options - Опции
   */
  constructor(
    element: HTMLElement,
    elements: DropdownElements,
    state: DropdownState,
    options: DropdownOptions
  ) {
    this.element = element
    this.elements = elements
    this.state = state
    this.options = options
  }

  public updateUI(): void {
    this.updateTriggerContent()
    this.updateCount()
    this.updateActiveButtons()
    this.updateTriggerActiveClass()
  }

  private updateTriggerContent(): void {
    if (this.options.multiple || !this.state.selectedValue) {
      this.elements.triggerContent.textContent = this.options.label || ''
      return
    }

    const selectedOption = this.options.options.find(
      (opt) => opt.value === this.state.selectedValue
    )

    if (selectedOption) {
      this.elements.triggerContent.textContent = selectedOption.label
    } else {
      this.elements.triggerContent.textContent = this.options.label || ''
    }
  }

  private updateCount(): void {
    if (!this.elements.triggerCount || !this.options.multiple) return

    const selectedArray = this.state.selectedValue as Array<string | number>
    this.elements.triggerCount.textContent = String(selectedArray.length || '')
  }

  private updateActiveButtons(): void {
    this.elements.optionButtons.forEach((button) => {
      const value = button.dataset.dropdownValue
      let isActive = false

      if (this.options.multiple) {
        const selectedArray = this.state
          .selectedValue as Array<DropdownOptionType>
        isActive = selectedArray.includes(value as DropdownOptionType)
      } else {
        isActive = value === String(this.state.selectedValue)
      }

      if (isActive) {
        button.classList.add('dropdown__option_active')
      } else {
        button.classList.remove('dropdown__option_active')
      }
    })

    // Обновляем кнопку "по умолчанию"
    if (this.elements.defaultOptionButton && this.options.multiple) {
      const selectedArray = this.state.selectedValue as Array<string | number>
      if (selectedArray.length === 0) {
        this.elements.defaultOptionButton.classList.add(
          'dropdown__option_active'
        )
      } else {
        this.elements.defaultOptionButton.classList.remove(
          'dropdown__option_active'
        )
      }
    }
  }

  private updateTriggerActiveClass(): void {
    const hasValue = this.options.multiple
      ? (this.state.selectedValue as Array<DropdownOptionType>).length > 0
      : Boolean(this.state.selectedValue)

    if (hasValue) {
      this.elements.trigger?.classList.add('dropdown__button_active')
    } else {
      this.elements.trigger?.classList.remove('dropdown__button_active')
    }
  }

  public filterOptions(): void {
    if (!this.options.withSearch || this.state.searchValue === '') {
      // Показываем все опции
      this.elements.optionButtons.forEach((button) => {
        button.classList.remove('is-hidden')
      })
      return
    }

    // Фильтруем опции по поиску
    this.elements.optionButtons.forEach((button: HTMLElement) => {
      const label = button.dataset.dropdownLabel?.toLowerCase() || ''
      button.classList.toggle(
        'is-hidden',
        !label.includes(this.state.searchValue)
      )
    })
  }

  public setContainerVisible(visible: boolean): void {
    if (visible) {
      this.elements.container?.classList.add('dropdown__container_visible')
      this.element.classList.add('dropdown_opened')
    } else {
      this.elements.container?.classList.remove('dropdown__container_visible')
      this.element.classList.remove('dropdown_opened')
    }
  }

  public clearSearch(): void {
    this.state.searchValue = ''
    if (this.elements.searchInput) {
      this.elements.searchInput.value = ''
    }
    this.filterOptions()
  }

  public emitChange(): void {
    if (this.options.onChange) {
      this.options.onChange(this.state.selectedValue)
    }

    // Отправляем кастомное событие для интеграции с другими компонентами
    const event = new CustomEvent('dropdown-change', {
      detail: {
        name: this.options.name,
        value: this.state.selectedValue
      },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }
}
